import {HasProjectMixin} from "./mixins/HasProjectMixin.jsx";
import {Model, StringHelper} from "@bryntum/schedulerpro";
import {STORE_KEYS} from "../../other/constants/StoreKeys.jsx";
import {userPreferences} from "../../react/context/bryntumContext.jsx";
import { NO_VALUE } from "../../other/constants/OtherKeys.jsx";
import { GRANTS } from "../../other/constants/PrivilegeTypes.jsx";

export class View extends HasProjectMixin(Model) {

  static relations = {
    folderRecord: {
      foreignKey: "folderId",
      foreignStore: STORE_KEYS.viewsFolderStore,
      relatedCollectionName: "views", // this key will be added to the parent to get child records
    },
  }

  static get fields() {
    return [
      { name: "creatorUserId", dataSource: "creator", type: "number", persist: false },
      { name: "creatorUserName", dataSource: "creator_name", persist: false},
      { name: "folderId", dataSource: "folder" },
      { name: "name", dataSource: "name" },
      { name: "iconId", dataSource: "icon", type: "number" },
      { name: "color", dataSource: "color" },
      { name: "order", type: "number", persist: false },
      { name: "viewType", dataSource: "view_type" },
      { name: "groupCollapsedState", dataSource: "group_collapsed_state", type: "boolean", defaultValue: false },
      // do not add type="object" to this property as bryntum has some issues with when applyingChangeSet is called, bryntum doesn't apply changes to this type of field
      { name: "viewState", dataSource: "view_state", defaultValue: {} },
      { name: "isDefaultView", dataSource: "is_default_view", type: "boolean", defaultValue: false },
      { name: "isShareToHQ", dataSource: "is_share_to_hq", type: "boolean", defaultValue: false },
      { name: "isShareToWorkforce", dataSource: "is_share_to_work_force", type: "boolean", defaultValue: false },
      { name: "isShareToPublic", dataSource: "is_share_to_public", type: "boolean", defaultValue: false },
      { name: "isShareToClient", dataSource: "is_share_to_client", type: "boolean", defaultValue: false },
      { name: "isBaseFolderChanged", dataSource: "is_base_folder_changed", type: "boolean", defaultValue: false },
      { name: "createdAt", dataSource: "created_at", persist: false },
      { name: "updatedAt", dataSource: "updated_at", persist: false },
      { name: "updatedById", dataSource: "updated_by", persist: false },
      { name: "updatedBy", persist: false },
      { name: "isOrderUpdateInc", defaultValue: 0, persist: false },
      { name: "viewDescription", dataSource: "description", type: "string"},
    ];
  }


  /* For default views the color value is null,  */
  get color() {
    return this.get('color') ?? "";
  }

  set color(value) {
    this.set("color", value);
  }

  get updatedBy() { // for now shared view is only updated by creator of it so this is not helpful
    if (this.project && this.updatedById) {
      return this.project.boardUsersStore.find((bu) => bu.userId === this.updatedById)?.name;
    }
    return null;
  }

  get isSharedView() {
    return this.isShareToHQ || this.isShareToWorkforce || this.isShareToPublic || this.isShareToClient;
  }

  set folderId(newFolderId) {
    if (!this.isGroupHeader && this.isAllowedToUpdateView) { // see gridRowBeforeDropFinalize in SwivuManagerViewEditor for this .isOwnView check is here
      if (this.get("folderId") && !this.isBaseFolderChanged) {
        this.set("isBaseFolderChanged", true);
      }
      // bryntum, set "!!novalue!!" if droppinga row into an unassigned/null folder
      this.set("folderId", newFolderId === NO_VALUE ? null: newFolderId);
    }
  }

  get folderId() {
    return this.get("folderId");
  }

  set order(newOrder) {
    this.setInUserPreference({ order: newOrder, folderId: this.folderId, isVisible: this.isVisible });
    // since sometime the order is updated in the userpreferences hence the listeners to the view store not called
    this.isOrderUpdateInc = (this.isOrderUpdateInc + 1);
  }

  get order() {
    return this.getUserPreference("order");
  }

  getUserPreference(key, defaultValue = undefined) {
    const prefObj = StringHelper.safeJsonParse(userPreferences.getSetting(`user-view-setting-${this.id}`));
    return prefObj?.[key] ?? defaultValue; // if this is null, then no need to fall back to the original field value
  }

  setInUserPreference(values) {
    userPreferences.updateSetting(`user-view-setting-${this.id}`, JSON.stringify(values));
  }

  get isOwnView() {
    if (this.isPhantom) {
      return true;
    }
    if (this.project) {
      const user = this.project?.boardUsersStore?.swivuCurrentUser;
      return this.creatorUserId === user?.id;
    }

    return false;
  }

  get isAllowedToUpdateView() {
    const user = this.project?.boardUsersStore?.swivuCurrentUser;
    return this.isOwnView || user?.can(GRANTS.grant_one);
  }

  canBeDeletedBy(user) {
    if (this.isDefaultView) return false;
    const isAdmin = user?.can(GRANTS.grant_one) ?? false;
    return isAdmin || this.isOwnView;
  }

  get isVisible() {
    return this.getUserPreference("isVisible", true);
  }

  set isVisible(value) {
    this.setInUserPreference({ isVisible: value, folderId: this.folderId, order: this.order });
  }
}
